/* ============================================================
   V5 — SEZ / EPZ Positioning Guide

   THE TAX CONVENTION, and why there are no rates below.

   An incentive is described by its SHAPE and by the authority that sets
   it. No percentage and no year count is written here, and that is a
   decision rather than an omission.

   Kenya moved these twice inside eighteen months. The Business Laws
   (Amendment) Act 2024, in force 27 December 2024, replaced a graduated
   framework running up to twenty-five years with a fixed ten-year
   benefit period from licence issue, and an SEZ (Amendment) Bill was
   before Parliament in 2026. Secondary sources still disagree about
   which schedule is current. A rate typed into a tool card has no date,
   no source and no freshness signal, so it cannot be seen to have gone
   stale — which is exactly how this project came to be telling operators
   to comply with a Legal Notice the Senate had annulled.

   structure.js already carries no tax rates for the same reason. This
   file now matches it.

   What does NOT rot is the qualitative distinction, and it is the thing
   that was wrong here:

     SEZ  — a REDUCED corporate rate. Currently 10%. Never zero.
     EPZ  — a genuine HOLIDAY. Zero for the first ten years, then a
            concessionary rate.

   Both SEZ zones below claimed a "tax holiday". An investor comparing
   Dongo Kundu against Athi River would have read the corporate position
   as equivalent when one is zero and the other is not — a difference
   worth real money on any EBITDA that justifies a zone in the first
   place. The rate is left out; the difference in kind is stated.

   Checked against source 8 August 2026. If a rate ever does belong
   here, it goes in regulations.js where the register dates it, sources
   it and ages it — not in prose on a card.
   ============================================================ */

import { html } from '../../shared/html.js';
import { icon } from '../../shared/icons.js';
import { PrintButton } from '../../components/PrintButton.js';
import { readable } from '../../shared/tone.js';
import { setPageMeasure } from '../../shared/measure.js';

const ZONES = {
  'mombasa-sez': {
    name: 'Mombasa SEZ (Dongo Kundu)',
    location: 'Dongo Kundu, Mombasa',
    status: 'Under development',
    developer: 'Kenya Ports Authority with private partners',
    incentives: [
      'Reduced corporate rate for the incentive period — a lower rate, not a holiday',
      'Duty exemption on capital goods',
      'VAT exemption on imports into the zone',
      'Stamp duty exemption',
      'Unrestricted dividend repatriation'
    ],
    advantages: [
      'Adjacent to Mombasa Port',
      'Direct SGR connection',
      'Freeport treatment for transhipment',
      'Customs-controlled zone'
    ],
    considerations: [
      'Infrastructure still under construction — phasing risk',
      'Limited tenant capacity in early phases',
      'Significant upfront capital commitment',
      'Environmental impact assessment required'
    ],
    bestFor: [
      'Regional distribution centres',
      'Cold chain operators',
      'Transhipment and consolidation',
      'Export-oriented manufacturing'
    ]
  },

  'tatu-city': {
    name: 'Tatu City SEZ',
    location: 'Kiambu County, Nairobi periphery',
    status: 'Operational',
    developer: 'Rendeavour (private)',
    incentives: [
      'SEZ status: a reduced corporate rate, not a holiday',
      'Streamlined single-window licensing',
      'Duty exemption on capital goods',
      'VAT deferment on imports'
    ],
    advantages: [
      'Roughly 30 km from Nairobi',
      'Near the SGR freight terminal',
      'Modern road infrastructure and reliable utilities',
      'Established tenant ecosystem'
    ],
    considerations: [
      'Higher land cost than legacy industrial zones',
      'Premium warehousing rates',
      'Not port-adjacent — dependent on corridor reliability',
      'Competition for skilled labour'
    ],
    bestFor: [
      'E-commerce fulfilment',
      'Light manufacturing',
      'Technology assembly',
      'Pharmaceutical distribution'
    ]
  },

  'naivasha-icd': {
    name: 'Naivasha ICD',
    location: 'Naivasha, Nakuru County',
    status: 'ICD operational, SEZ designation pending',
    developer: 'Kenya Railways Corporation',
    incentives: [
      'SEZ designation in the pipeline, not yet gazetted',
      'Operational SGR freight terminal',
      'Lower land cost than Nairobi or Mombasa'
    ],
    advantages: [
      'SGR dry port with direct Mombasa line haul',
      'Strategic midpoint on the Northern Corridor',
      'Agricultural hinterland access',
      'Lower labour cost base'
    ],
    considerations: [
      'SEZ status not yet gazetted — do not underwrite on it',
      'Limited social infrastructure for staff',
      'Last-mile road connectivity gaps',
      'Water and power reliability need verification'
    ],
    bestFor: [
      'Agricultural processing',
      'Bulk commodity handling',
      'Regional consolidation',
      'Cost-sensitive manufacturing'
    ]
  },

  'athi-river-epz': {
    name: 'Athi River EPZ',
    location: 'Athi River, Machakos County',
    status: 'Operational',
    developer: 'Export Processing Zones Authority',
    incentives: [
      'Ten-year corporate tax holiday — genuinely zero, unlike an SEZ — then a concessionary rate',
      'Duty drawback on inputs',
      'No VAT on local purchases',
      'Single-window licensing through EPZA'
    ],
    advantages: [
      'A109 Mombasa Road access',
      'Established industrial ecosystem',
      'Mature labour market',
      'Proven regulatory pathway'
    ],
    considerations: [
      'Export requirement limits domestic market access',
      'Mombasa Road congestion affects lead times',
      'Higher utility costs than newer zones',
      'Ageing infrastructure in parts of the estate'
    ],
    bestFor: [
      'Garment and textile export',
      'Leather processing',
      'Light assembly for export',
      'Horticulture packaging'
    ]
  }
};

export function SezPage() {
  const page = document.createElement('div');
  page.className = 'container-narrow page animate-fade-in';

  page.innerHTML = html`
    <header class="page-head">
      <h1>SEZ / EPZ Positioning Guide</h1>
      <p>Four zones, compared on incentives, logistics and the risks worth pricing in.</p>
    </header>
    <div id="sez-body"></div>
  `;

  renderSelector(page.querySelector('#sez-body'));
  return page;
}

function renderSelector(container) {
  setPageMeasure(container, 'wide');
  container.replaceChildren();
  const list = document.createElement('div');
  /* tool-grid, not stack: these are three or four sibling choices, and
     stacking them put one 640px-wide card per row with 740px of empty
     page beside it. The same cards as an auto-fill grid are one row. */
  list.className = 'tool-grid';

  for (const [key, zone] of Object.entries(ZONES)) {
    const operational = zone.status === 'Operational';
    const color = operational ? 'var(--ca-positive)' : 'var(--ca-warning)';

    const card = document.createElement('button');
    card.type = 'button';
    card.className = 'card card-link';
    card.innerHTML = html`
      <div class="card-stack row" style="align-items: flex-start;">
        <div style="flex:1; min-width:0;">
          <h3 style="font-size: var(--ca-text-base); margin-bottom: var(--ca-space-1);">${zone.name}</h3>
          <p style="font-size: var(--ca-text-xs); color: var(--ca-text-tertiary);">${zone.location}</p>
        </div>
        <span class="badge" style="background: color-mix(in srgb, ${color} 12%, transparent); color: ${readable(color)};">
          ${zone.status}
        </span>
      </div>
    `;
    card.addEventListener('click', () => renderDetail(container, key));
    list.appendChild(card);
  }

  container.appendChild(list);
}

function renderDetail(container, key) {
  setPageMeasure(container, 'reading');
  const zone = ZONES[key];
  container.replaceChildren();

  const back = document.createElement('button');
  back.type = 'button';
  back.className = 'btn btn-ghost no-print';
  back.style.marginBottom = 'var(--ca-space-4)';
  back.innerHTML = `${icon('left', 16)}<span>All zones</span>`;
  back.addEventListener('click', () => renderSelector(container));
  container.appendChild(back);

  const detail = document.createElement('div');
  detail.id = 'sez-detail';

  const block = (title, color, items) => html`
    <section class="card avoid-break" style="margin-bottom: var(--ca-space-3);">
      <h3 style="font-size: var(--ca-text-sm); color: ${readable(color)}; margin-bottom: var(--ca-space-2);">${title}</h3>
      <ul style="margin: 0; padding-left: var(--ca-space-5); font-size: var(--ca-text-sm);
                 color: var(--ca-text-secondary); line-height: var(--ca-leading-normal);">
        ${items.map((i) => html`<li>${i}</li>`)}
      </ul>
    </section>
  `;

  detail.innerHTML = html`
    <h2 style="font-size: var(--ca-text-lg); margin-bottom: var(--ca-space-1);">${zone.name}</h2>
    <p style="font-size: var(--ca-text-sm); color: var(--ca-text-tertiary); margin-bottom: var(--ca-space-5);">
      ${zone.location} · ${zone.status} · ${zone.developer}
    </p>
    ${block('Incentives', 'var(--ca-positive)', zone.incentives)}
    ${block('Logistics advantages', 'var(--ca-chart-1)', zone.advantages)}
    ${block('Considerations', 'var(--ca-warning)', zone.considerations)}
    <section class="card avoid-break" style="margin-bottom: var(--ca-space-3);">
      <h3 style="font-size: var(--ca-text-sm); margin-bottom: var(--ca-space-3);">Best suited to</h3>
      <div class="row" style="flex-wrap: wrap; gap: var(--ca-space-2);">
        ${zone.bestFor.map((b) => html`<span class="chip">${b}</span>`)}
      </div>
    </section>
    <section class="card card-tinted avoid-break">
      <h3 style="font-size: var(--ca-text-sm); margin-bottom: var(--ca-space-2);">How to decide</h3>
      <p style="font-size: var(--ca-text-xs); color: var(--ca-text-tertiary);">
        Choose ${zone.name} if your operation matches the categories above, the incentives
        outweigh the considerations for your specific case, and your capital timeline matches
        the zone’s development status. Next step: approach ${zone.developer} for a site visit,
        the tariff schedule and lease terms, then apply for an investment certificate.
      </p>
    </section>
  `;

  container.appendChild(detail);

  detail.appendChild(
    PrintButton({
      targetId: 'sez-detail',
      title: `${zone.name} — Positioning Assessment`,
      subtitle: `${zone.location} · ${zone.status}`
    })
  );
}
